import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useHioBuyOnly } from '../../lib/marketMode';
import { buyerNav, navLabel } from '../../lib/navItems';

// Persistent trader bottom nav — an app shell. Rendered globally (from App.js) so it
// stays FIXED across the trader's pages (home, products, managed order),
// not just inside the dashboard. Mobile-only: the `db-bottom-nav` CSS hides it on
// desktop, where the dashboard's top tabs take over.
//
// The center "+" opens a request-type sheet (standard / managed / idea) — the trader
// picks the type first, then lands in the right NEW flow (never the old combined form).

// Labels come from lib/navItems.js — the same words as the site navbar, the
// market bar and the dashboard tabs. «الرئيسية» here used to open /dashboard
// while «الرئيسية» in the navbar opened the store; it is «لوحتي» now, and
// «طلباتي» (RFQs here, market orders in the market bar) is «طلبات التسعير».

const SHEET = {
  ar: { head: 'ارفع طلبك',
    standardT: 'طلب عادي', standardS: 'لمنتج واضح وتحتاج عروض مباشرة من الموردين',
    managedT: 'الطلب المُدار', managedS: 'مَعبر تبحث لك وتتكفّل بكل خطوة حتى باب بيتك',
    ideaT: 'تصنيع فكرة', ideaS: 'حوّل فكرتك إلى طلب احترافي بمساعدة الذكاء الاصطناعي' },
  en: { head: 'Submit your request',
    standardT: 'Standard request', standardS: 'For a known product with direct offers from suppliers',
    managedT: 'Managed order', managedS: 'Maabar sources and handles every step to your door',
    ideaT: 'Build your idea', ideaS: 'Turn your idea into a professional request with AI' },
  zh: { head: '提交您的需求',
    standardT: '标准请求', standardS: '针对明确产品，直接获取供应商报价',
    managedT: '托管订单', managedS: 'Maabar 全程为您寻源直至送货上门',
    ideaT: '实现您的创意', ideaS: '借助 AI 将创意转化为专业请求' },
};

// «المزيد»: the places that are not on the bar. Purchases first — it is
// top-level everywhere else (lib/navItems.js). Messages has its own tab
// (owner, 2026-10-21: «ابيه واضح… بالشريط السفلي», six tabs like the app).
const MORE = {
  ar: [{ tab: 'samples', label: 'العينات' }, { tab: 'product-inquiries', label: 'استفسارات المنتجات' }, { tab: 'settings', label: 'الإعدادات' }],
  en: [{ tab: 'samples', label: 'Samples' }, { tab: 'product-inquiries', label: 'Product inquiries' }, { tab: 'settings', label: 'Settings' }],
  zh: [{ tab: 'samples', label: '样品' }, { tab: 'product-inquiries', label: '产品咨询' }, { tab: 'settings', label: '设置' }],
};

export default function BuyerBottomNav({ lang = 'ar' }) {
  const nav = useNavigate();
  const loc = useLocation();
  const isAr = lang === 'ar';
  const font = { fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' };
  const [moreOpen, setMoreOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const sh = SHEET[lang] || SHEET.ar;
  const hioBuyOnly = useHioBuyOnly().hioBuyOnly;   // lib/marketMode.js
  const places = Object.fromEntries(buyerNav({ hioBuyOnly }).map((it) => [it.key, it.path]));
  const productsPath = places.store;
  const more = [
    { path: places.purchases, label: navLabel('purchases', lang) },
    ...(MORE[lang] || MORE.ar).map((m) => ({ ...m, path: `/dashboard?tab=${m.tab}` })),
  ];
  const params = new URLSearchParams(loc.search);
  const tab = params.get('tab') || 'overview';
  const onDash = loc.pathname === '/dashboard' || loc.pathname === '/dashboard/';
  const active = (id) => {
    if (id === 'home') return onDash && tab === 'overview';
    if (id === 'products') return loc.pathname.startsWith(productsPath) && !loc.pathname.startsWith('/market/orders');
    if (id === 'requests') return onDash && tab === 'requests';
    if (id === 'messages') return loc.pathname === places.messages || loc.pathname.startsWith('/chat');
    if (id === 'more') return (onDash && ['samples', 'product-inquiries', 'settings', 'direct-orders'].includes(tab))
      || loc.pathname.startsWith('/market/orders');
    return false;
  };

  const go = (id) => {
    setMoreOpen(false);
    if (id === 'home') nav('/dashboard');
    else if (id === 'products') nav(productsPath);
    else if (id === 'requests') nav('/dashboard?tab=requests');
    else if (id === 'messages') nav(places.messages);
    else if (id === 'more') setMoreOpen((o) => !o);
    else if (id === 'new') { setMoreOpen(false); setSheetOpen(true); }
  };

  const pickType = (type) => {
    setSheetOpen(false);
    if (type === 'standard') nav('/requests');            // standard RFQ — suppliers compete
    else if (type === 'managed') nav('/request');         // the NEW managed wizard
    else if (type === 'idea') window.dispatchEvent(new CustomEvent('maabar:open-idea'));
  };

  const items = [
    { id: 'home', label: navLabel('dashboard', lang) }, { id: 'products', label: navLabel('store', lang) },
    { id: 'new', label: null },
    { id: 'requests', label: navLabel('quotes', lang) }, { id: 'messages', label: navLabel('messages', lang) },
    { id: 'more', label: navLabel('more', lang) },
  ];

  const Overlay = ({ onClose }) => (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1998, background: 'rgba(0,0,0,0.4)' }} />
  );
  const SheetRow = ({ title, sub, onClick, primary }) => (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: isAr ? 'right' : 'left',
      background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 15, marginBottom: 10, cursor: 'pointer',
      [isAr ? 'borderRight' : 'borderLeft']: primary ? '3px solid var(--bronze, #8B7355)' : undefined }}>
      <span style={{ flex: 1 }}>
        <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3, ...font }}>{title}</span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-disabled)', lineHeight: 1.5, ...font }}>{sub}</span>
      </span>
      <span style={{ color: 'var(--text-disabled)', fontSize: 16 }}>{isAr ? '←' : '→'}</span>
    </button>
  );

  return (
    <>
      {/* Request-type sheet */}
      {sheetOpen && <Overlay onClose={() => setSheetOpen(false)} />}
      {sheetOpen && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1999, background: 'var(--bg-base)', borderRadius: '24px 24px 0 0', padding: '12px 20px calc(24px + env(safe-area-inset-bottom, 0px))', animation: 'slideUp 0.2s ease' }}>
          <div style={{ width: 40, height: 4, background: 'var(--border-default, #ccc)', borderRadius: 2, margin: '0 auto 18px' }} />
          <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-disabled)', textAlign: isAr ? 'right' : 'left', marginBottom: 12, ...font }}>{sh.head}</p>
          <SheetRow title={sh.standardT} sub={sh.standardS} onClick={() => pickType('standard')} />
          <SheetRow title={sh.managedT} sub={sh.managedS} onClick={() => pickType('managed')} primary />
          <SheetRow title={sh.ideaT} sub={sh.ideaS} onClick={() => pickType('idea')} />
        </div>
      )}

      <nav className="db-bottom-nav" dir={isAr ? 'rtl' : 'ltr'}>
        {/* More menu */}
        {moreOpen && <Overlay onClose={() => setMoreOpen(false)} />}
        {moreOpen && (
          <div style={{ position: 'fixed', bottom: 'calc(56px + env(safe-area-inset-bottom, 0px))', left: 0, right: 0, zIndex: 1999,
            background: 'var(--bg-overlay)', borderRadius: '16px 16px 0 0', padding: '16px 20px 8px', borderTop: '1px solid var(--border)', animation: 'slideUp 0.2s ease' }}>
            {more.map((m, i, arr) => (
              <button key={m.path} onClick={() => { setMoreOpen(false); nav(m.path); }}
                style={{ display: 'block', width: '100%', textAlign: isAr ? 'right' : 'left', padding: '13px 0', background: 'none', border: 'none',
                  borderBottom: i < arr.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                  color: `${loc.pathname}${loc.search}` === m.path ? 'var(--text-primary)' : 'var(--text-secondary)', fontSize: 15, cursor: 'pointer', ...font }}>
                {m.label}
              </button>
            ))}
          </div>
        )}

        {items.map((item) => {
          if (item.id === 'new') {
            return (
              <button key="new" onClick={() => go('new')} style={{ flex: 1, background: 'none', border: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <span style={{ width: 42, height: 42, borderRadius: '50%', background: 'var(--text-primary)', color: 'var(--bg-base, #fff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 300, marginTop: -4, lineHeight: 1 }}>+</span>
              </button>
            );
          }
          const isActive = active(item.id);
          return (
            <button key={item.id} onClick={() => go(item.id)} style={{ flex: 1, minWidth: 0, padding: '10px 2px 6px', background: 'none', border: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, cursor: 'pointer', color: isActive ? 'var(--text-primary)' : 'var(--text-disabled)' }}>
              <span style={{ fontSize: 10.5, letterSpacing: 0, whiteSpace: 'nowrap', fontWeight: isActive ? 700 : 400, ...font }}>{item.label}</span>
              {isActive && <div style={{ width: 16, height: 2, background: 'var(--text-primary)', borderRadius: 1 }} />}
            </button>
          );
        })}
      </nav>
    </>
  );
}
